import { Injectable } from '@angular/core';

import { catchError, forkJoin, map, of } from 'rxjs';

import { environment } from '../../../../../environments/environment';

import { ReportsHttpClient, ReportsService } from '../../../../core/services/reports.service';
import { UtilityService } from '../../../../core/services/utility.service';
import { SessionStorageService } from '../../../../core/services/session-storage.service';

@Injectable({
  providedIn: 'root'
})
export class DashboardService {
  private TIP_OF_THE_WEEK_FILE = '/assets/configuration/tipOfTheWeek.json' ;

  constructor(
    private httpClient: ReportsHttpClient,
    private reportsService: ReportsService,
    private utilityService: UtilityService,
  ) { }

  getStudentsNotLoggedInList(includeCoAccess = true) {
    let teacherID = this.reportsService.getSelectedTeacherForReports().userID ;
    let schoolID = this.reportsService.getSelectedSchoolForReports().schoolID ;
    let districtID = this.reportsService.getSelectedDistrictForReports().districtID ;

    const reqOpts = {
      withCredentials: true,
      params: {
        includeCoAccess: includeCoAccess.toString()
      },
      headers: {
        'Access-Control-Allow-Origin': '*',
      },
    }

    return this.httpClient.get(`${environment.dataServicesURL}manage/reports/district/${districtID}/school/${schoolID}/teacher/${teacherID}/StudentsNotLoggedIn/List`, reqOpts);
  }

  getStudentsNotLoggedInCount(includeCoAccess = true) {
    let teacherID = this.reportsService.getSelectedTeacherForReports().userID ;
    let schoolID = this.reportsService.getSelectedSchoolForReports().schoolID ;
    let districtID = this.reportsService.getSelectedDistrictForReports().districtID ;

    const reqOpts = {
      withCredentials: true,
      params: {
        includeCoAccess: includeCoAccess.toString()
      },
      headers: {
        'Access-Control-Allow-Origin': '*',
      },
    }

    return this.httpClient.get(`${environment.dataServicesURL}manage/reports/district/${districtID}/school/${schoolID}/teacher/${teacherID}/StudentsNotLoggedIn/Count`, reqOpts);
  }

  getMostCommonCurrentUnit(includeCoAccess = true) {
    let teacherID = this.reportsService.getSelectedTeacherForReports().userID ;
    let schoolID = this.reportsService.getSelectedSchoolForReports().schoolID ;
    let districtID = this.reportsService.getSelectedDistrictForReports().districtID ;

    const reqOpts = {
      withCredentials: true,
      params: {
        includeCoAccess: includeCoAccess.toString()
      },
      headers: {
        'Access-Control-Allow-Origin': '*',
      },
    }

    return this.httpClient.get(`${environment.dataServicesURL}manage/reports/district/${districtID}/school/${schoolID}/teacher/${teacherID}/mostCommonCurrentUnit`, reqOpts);
  }

  getNumberOfEnrolledStudentsForFullProduct(includeCoAccess = true) {
    let teacherID = this.reportsService.getSelectedTeacherForReports().userID ;
    let schoolID = this.reportsService.getSelectedSchoolForReports().schoolID ;
    let districtID = this.reportsService.getSelectedDistrictForReports().districtID ;

    const reqOpts = {
      withCredentials: true,
      params: {
        includeCoAccess: includeCoAccess.toString()
      },
      headers: {
        'Access-Control-Allow-Origin': '*',
      },
    }

    return this.httpClient.get(`${environment.dataServicesURL}manage/reports/district/${districtID}/school/${schoolID}/teacher/${teacherID}/numEnrolledStudentsFullProduct`, reqOpts);
  }

  getNumberOfPointsEarned(includeCoAccess = true) {
    let teacherID = this.reportsService.getSelectedTeacherForReports().userID ;
    let schoolID = this.reportsService.getSelectedSchoolForReports().schoolID ;
    let districtId = this.reportsService.getSelectedDistrictForReports().districtID ;

    const reqOpts = {
      withCredentials: true,
      params: {
        includeCoAccess: includeCoAccess.toString()
      },
      headers: {
        'Access-Control-Allow-Origin': '*',
      },
    }

    return this.httpClient.get(`${environment.dataServicesURL}manage/reports/district/${districtId}/school/${schoolID}/teacher/${teacherID}/numPointsEarned`, reqOpts);
  }

  getPreviousWeeksSessionTimeStats(includeCoAccess = true) {
    let teacherID = this.reportsService.getSelectedTeacherForReports().userID ;
    let schoolID = this.reportsService.getSelectedSchoolForReports().schoolID ;
    let districtId = this.reportsService.getSelectedDistrictForReports().districtID ;

    const reqOpts = {
      withCredentials: true,
      params: {
        includeCoAccess: includeCoAccess.toString()
      },
      headers: {
        'Access-Control-Allow-Origin': '*',
      },
    }

    return this.httpClient.get(`${environment.dataServicesURL}manage/reports/district/${districtId}/school/${schoolID}/teacher/${teacherID}/previousWeeksTimeGoalStats`, reqOpts);
  }

  getAvgWeeklyUsagePrevious(includeCoAccess = true) {
    let teacherID = this.reportsService.getSelectedTeacherForReports().userID ;
    let schoolID = this.reportsService.getSelectedSchoolForReports().schoolID ;
    let districtID = this.reportsService.getSelectedDistrictForReports().districtID ;

    const reqOpts = {
      withCredentials: true,
      params: {
        includeCoAccess: includeCoAccess.toString()
      },
      headers: {
        'Access-Control-Allow-Origin': '*',
      },
    }

    return this.httpClient.get(`${environment.dataServicesURL}manage/reports/district/${districtID}/school/${schoolID}/teacher/${teacherID}/avgWeeklyUsagePrevious`, reqOpts);
  }

  getAvgWeeklyUsageAll(includeCoAccess = true) {
    let teacherID = this.reportsService.getSelectedTeacherForReports().userID ;
    let schoolID = this.reportsService.getSelectedSchoolForReports().schoolID ;
    let districtID = this.reportsService.getSelectedDistrictForReports().districtID ;

    const reqOpts = {
      withCredentials: true,
      params: {
        includeCoAccess: includeCoAccess.toString()
      },
      headers: {
        'Access-Control-Allow-Origin': '*',
      },
    }

    return this.httpClient.get(`${environment.dataServicesURL}manage/reports/district/${districtID}/school/${schoolID}/teacher/${teacherID}/avgWeeklyUsageAll`, reqOpts);
  }

  getTipOfTheWeek() {
    let weekNumber = this.utilityService.getWeekNumber() ;

    // If weekNumber is 53, use the tip for the 52nd week
    if (weekNumber === 53) weekNumber = 52 ;

    // Read in the tip of the week file and return the tip of the week
    return this.httpClient.get(this.TIP_OF_THE_WEEK_FILE).pipe(
      map((resp: any) => {
        if (resp.tips[weekNumber] !== null)
        {
          return resp.tips[weekNumber] ;
        }
        else
        {
          return '--' ;
        }
      }),
      catchError((err) => {
        return '--' ;
      })
    )
  }

  getSystemDashboardData(includeCoAccess = true) {
    const teacherID = this.reportsService.getSelectedTeacherForReports().userID ;
    const schoolID = this.reportsService.getSelectedSchoolForReports().schoolID ;
    const selectedDistrict = this.reportsService.getSelectedDistrictForReports() ;
    const checkCleverReqs = this.reportsService.checkCleverRequirements(selectedDistrict, schoolID, teacherID) ;
    if (checkCleverReqs !== null)
    {
      return checkCleverReqs ;
    }

    if (teacherID === 0)
    {
      return of({
        erred: true,
        message: 'Please select a Teacher',
      }) ;
    }

    return forkJoin({
      studentsNotStarted: this.getStudentsNotLoggedInCount(includeCoAccess),
      studentsEnrolled: this.getNumberOfEnrolledStudentsForFullProduct(includeCoAccess),
      totalPointsEarned: this.getNumberOfPointsEarned(includeCoAccess),
      tipOfTheWeek: this.getTipOfTheWeek(),
      previousWeekSessions: this.getPreviousWeeksSessionTimeStats(includeCoAccess),
      avgWeeklyUsagePrevious: this.getAvgWeeklyUsagePrevious(includeCoAccess),
      avgWeeklyUsageAll: this.getAvgWeeklyUsageAll(includeCoAccess),
      mostCommonUnit: this.getMostCommonCurrentUnit(includeCoAccess),
    }) ;
  }

  getParentDashboardDataForStudent(studentId: number) {
    const reqOpts = {
      withCredentials: true,
      headers: {
        'Access-Control-Allow-Origin': '*',
      },
    }

    return this.httpClient.get(`${environment.dataServicesURL}manage/reports/student/${studentId}/parentDashboard`, reqOpts);
  }
}
